import { render, type ComponentType } from "preact";
import { h } from "preact";
import type { IslandSpec, PayloadOf } from "@brave-new-world/shared";

/**
 * Island readers/mounters driven by the shared `Islands` contract.
 *
 * Before this, each island hand-rolled the same four steps — find the payload
 * element, `textContent`, `try { JSON.parse } catch`, cast to a separately
 * imported type — and each spelled its two ids as independent literals. The cast
 * was the weak point: `JSON.parse(text) as LabsSearchInit` asserts a shape nobody
 * checked, so a server-side rename surfaced as `undefined` deep inside a
 * component rather than at the seam.
 *
 * These helpers do not make the payload runtime-validated (it still crosses as
 * JSON and is still asserted), but they make the *ids* impossible to disagree on
 * and the payload type impossible to pair with the wrong island.
 */

/**
 * Read an island's JSON payload. Returns undefined when the island is absent or
 * malformed — every caller has a sensible do-nothing path, and a throw here would
 * take down page init for an island that simply is not on this page.
 */
export function readIsland<S extends IslandSpec<unknown>>(spec: S): PayloadOf<S> | undefined {
  const el = document.getElementById(spec.payloadId);
  if (!el?.textContent) return undefined;
  try {
    return JSON.parse(el.textContent) as PayloadOf<S>;
  } catch {
    console.error(`island "${spec.name}": payload #${spec.payloadId} is not valid JSON`);
    return undefined;
  }
}

/**
 * Read an island's payload and render its component into the island's root.
 * No-ops when the root is absent (i.e. this is not that page).
 *
 * `fallback` is used when the island is present but its payload is missing or
 * unparseable — rendering with an empty vocabulary beats not rendering at all.
 */
export function mountIsland<S extends IslandSpec<unknown>>(
  spec: S,
  Component: ComponentType<PayloadOf<S> & Record<string, unknown>>,
  fallback: PayloadOf<S>
): void {
  const container = document.getElementById(spec.rootId);
  if (!container) return;
  const payload = readIsland(spec) ?? fallback;
  render(h(Component, payload as PayloadOf<S> & Record<string, unknown>), container);
}
